<template>
    <ies-form-field :field="field">
        <v-autocomplete
            v-model="input"
            :items="policiesWithNew"
            :required="this.field.required"
            :disabled="this.field.disabled"
            outlined
            dense
            @change="onPolicyChange"
        ></v-autocomplete>

        <v-card v-if="showAddCustomPolicy" class="mt-2 mb-2 pa-3">
            <v-card-title class="subtitle-1">New Policy</v-card-title>
            <v-card-text>
                <v-row class="font-weight-bold caption mb-1" no-gutters>
                    <v-col cols="6">Amount</v-col>
                    <v-col cols="6">When</v-col>
                </v-row>
                <v-row v-for="(scheduleRow, index) in scheduleRows" :key="index" no-gutters align="center" class="mb-2">
                    <v-col cols="6" class="pr-2">
                        <v-text-field
                            v-model="scheduleRow.amount"
                            :placeholder="index === 0 ? 'e.g. 35% of total' : (index === scheduleRows.length - 1 ? 'Final balance' : '')"
                            outlined
                            dense
                            hide-details
                        ></v-text-field>
                    </v-col>
                    <v-col cols="5" class="pr-2">
                        <v-row no-gutters align="center">
                            <v-col>
                                <v-select
                                    v-model="scheduleRow.when"
                                    :items="whenOptions"
                                    outlined
                                    dense
                                    hide-details
                                ></v-select>
                            </v-col>
                            <v-col v-if="scheduleRow.when === 'days_before'" cols="auto" class="pl-2 d-flex align-center">
                                <v-text-field
                                    v-model="scheduleRow.days"
                                    type="number"
                                    min="1"
                                    outlined
                                    dense
                                    hide-details
                                    style="width: 80px"
                                ></v-text-field>
                                <span class="ml-2 body-2 grey--text text--darken-1">days before arrival</span>
                            </v-col>
                        </v-row>
                    </v-col>
                    <v-col cols="1" class="text-center">
                        <v-btn icon small @click="removeScheduleRow(index)" :disabled="scheduleRows.length === 1">
                            <v-icon small>close</v-icon>
                        </v-btn>
                    </v-col>
                </v-row>
                <v-btn small text @click="addScheduleRow">+ Add row</v-btn>
            </v-card-text>
        </v-card>

        <v-btn v-if="allowTextEdit" icon small @click="editText = !editText" :title="editText ? 'Lock policy text' : 'Manually edit policy text'">
            <v-icon small>{{ editText ? 'lock_open' : 'lock' }}</v-icon>
        </v-btn>
        <v-textarea
            v-model="text"
            :required="this.field.required"
            :disabled="this.field.disabled || !editText || !allowTextEdit"
            >
            
        </v-textarea>
    </ies-form-field>
</template>

<script>
export default {
    name: 'ies-input-rate-type-payment-policy-key',
    props: ['field', 'page'],
    components: {
        'ies-form-field': require('./FormField.vue').default,
    },
    data () {
        return {
            input: parseInt(this.field.value || this.field.oldValue) || null,
            policies: [],
            hotelKey: null,
            rateTypeKey: null,
            hotelName: null,
            prefix: null,
            editText: false,
            text: null,
            showAddCustomPolicy: false,
            scheduleRows: [
                { amount: null, when: null, days: null },
                { amount: null, when: null, days: null }
            ],
            whenOptions: [
                { text: 'Straight after booking', value: 'after_booking' },
                { text: 'Days before arrival', value: 'days_before' },
                { text: 'On checkout', value: 'on_checkout' },
            ]
        }
    },
    computed: {
        submissionData: function() {
            let policyMode = this.showAddCustomPolicy ? 3 : this.getModeForInput(this.input);
            return {
                policy_mode: policyMode,
                policy_text: this.text,
                rateTypeKey: this.rateTypeKey
            };
        },

        allowTextEdit: function() {
            if (this.showAddCustomPolicy) {
                return true;
            }
            let mode = this.getModeForInput(this.input);
            return mode === 3;
        },

        policiesWithNew: function() {
            return this.policies.concat([{ text: 'New Policy...', value: '__new__' }]);
        },

        generatedText: function() {
            let whenTextMap = {
                'after_booking': 'straight after booking',
                'days_before': 'days',
                'on_checkout': 'on checkout'
            };

            let instalments = this.scheduleRows.map(function(scheduleRow) {
                return {
                    amount: scheduleRow.amount,
                    when: whenTextMap[scheduleRow.when] || scheduleRow.when,
                    when_days: scheduleRow.days
                };
            });

            let body = this.generatePolicyText(instalments, this.hotelName);
            
            return this.prefix ? this.prefix + ' ' + body : body;
        }
    },
    watch: {
        input: function(value) {
            this.updateTextFromPolicy(value);
        },

        submissionData: {
            deep: true,
            handler: function(value) {
                this.field.value = value;
            }
        },

        scheduleRows: {
            deep: true,
            handler: function() {
                if (this.showAddCustomPolicy && !this.editText) {
                    this.text = this.generatedText;
                }
            }
        }
    },

    methods: {
        setRateTypeKeyFromUrl: function(){
            if (!this.page || !this.page.edit_url){
                return;
            }

            let bits = this.page.edit_url.split('/');
            
            let foundIndex = null;
            
            bits.forEach(function(bit, index){
                if (bit == '{RateType.rate_type_key}'){
                    foundIndex = index;
                }
            });

            if (foundIndex !== null){
                let urlBits = location.pathname.split('/').splice(1);
                this.rateTypeKey = parseInt(urlBits[foundIndex]);
            }
        },

        setHotelKeyFromUrl: function(){
            // Only try to use the data if it is set, otherwise it will simply crash
            if (!this.page || !this.page.edit_url){
                return;
            }

            // Find out if the URL has a hotel ID in it
            // by checking the edit url pattern for one
            // and making a note of which part it is in
            let bits = this.page.edit_url.split('/');
            let foundIndex = null;
            
            bits.forEach(function(bit, index){
                if (bit == '{Hotel.hotel_key}' || bit == '{RateType.hotel_key}'){
                    foundIndex = index;
                }
            });

            // If we found a hotel key in the url pattern
            // then extract the value from the current page
            // url at that part
            if (foundIndex !== null){
                let urlBits = location.pathname.split('/').splice(1);
                this.hotelKey = parseInt(urlBits[foundIndex]);
            }
        },

        onPolicyChange: function(value) {
            if (value === '__new__') {
                this.input = null;
                this.showAddCustomPolicy = true;
                this.text = this.generatedText;
            } else {
                this.showAddCustomPolicy = false;
            }
        },

        getModeForInput: function(inputValue) {
            let selectedPolicy = this.policies.find(function(policy) {
                return policy.value === inputValue;
            });

            return selectedPolicy ? selectedPolicy.mode : null;
        },

        updateTextFromPolicy: function(value) {
            if (!value || value === '__new__') {
                return;
            }

            let selectedPolicy = this.policies.find(function(policy) {
                return policy.value === value;
            });

            // if mode is 3 then the prefix is already part of the text
            if (selectedPolicy) {
                this.text = (selectedPolicy.mode !== 3 && this.prefix)
                    ? this.prefix + ' ' + selectedPolicy.text
                    : selectedPolicy.text;
            }
        },

        generatePolicyText: function(instalments, hotelName) {
            let result = '';

            instalments.forEach(function(instalment) {
                if (!instalment.amount || !instalment.when) {
                    return;
                }

                let amount = instalment.amount.charAt(0).toUpperCase() + instalment.amount.slice(1);
                let whenText;
                
                if (instalment.when === 'days') {
                    whenText = instalment.when_days + ' days before arrival';
                } else {
                    whenText = instalment.when;
                }

                result += amount + ' to be paid to ' + hotelName + ' ' + whenText + '. ';
            });
            return result.trimEnd();
        },

        addScheduleRow: function(){
            this.scheduleRows.push({ amount: null, when: null, days: null });
        },

        removeScheduleRow: function(index){
            this.scheduleRows.splice(index, 1);
        },

        getPolicies: function() {
            axios.get('/api/rate-type-payment-policies/' + this.hotelKey).then((response) => {
                this.hotelName = response.data.hotelName;
                this.prefix = response.data.prefix;

                this.policies = response.data.policies.map(function(policy) {
                    return {
                        text: policy.text,
                        value: policy.key !== null ? policy.key : ('mode_' + policy.mode),
                        mode: policy.mode
                    };
                });

                this.updateTextFromPolicy(this.input);
            });
        }
    },
    created: function(){
        this.setRateTypeKeyFromUrl();
        this.setHotelKeyFromUrl();
        this.getPolicies();
    }
}
</script>
